import {Helmet} from 'react-helmet-async';
import {Redirect, Route, Switch} from 'react-router-dom';
import {NotAuthorizedError} from '@core/components/AppError';
import {FetchedUser} from '@core/graphql-api';
import {createOrgPath, isOrgBrand} from '@core/lib/organizations';
import Loading from '@core/ui/Loading';
import Layout from '@analytics/components/Layout';
import {useOrganizationQuery} from '@analytics/graphql-api';
import {AdvisorIndex} from '../unified/advisor';
import {CampaignsIndex} from '../unified/campaigns';
import {ManageIndex} from '../unified/manage';
import {MeasureIndex} from '../unified/measure';
import {PodcastsIndex} from '../unified/podcasts';
import {ReportsIndex} from '../unified/reports';
import {ResearchIndex} from '../unified/research';
import {BrandHome} from './BrandHome';

type Props = {
  user: FetchedUser;
  match: {
    params: {slug: string};
  };
};

export function BrandIndex({
  user,
  match: {
    params: {slug},
  },
}: Props) {
  const {data, loading, error} = useOrganizationQuery({
    variables: {
      slug,
    },
  });

  const organization = data?.me?.organization;

  if (loading) {
    return <Loading fixed />;
  }

  if (error || !organization) {
    return <NotAuthorizedError user={user} />;
  }

  if (!isOrgBrand(organization)) {
    return <Redirect to={createOrgPath(organization)} />;
  }

  return (
    <>
      <Helmet>
        <title>{organization.name} | Ad Analytics</title>
      </Helmet>
      <Layout user={user} organization={organization}>
        <Switch>
          {[
            {exact: true, path: '/b/:slug', Component: BrandHome},
            {path: '/b/:slug/podcasts', Component: PodcastsIndex},
            {path: '/b/:slug/campaigns', Component: CampaignsIndex},
            {path: '/b/:slug/research', Component: ResearchIndex},
            {path: '/b/:slug/advisor', Component: AdvisorIndex},
            {path: '/b/:slug/measure', Component: MeasureIndex},
            {path: '/b/:slug/manage', Component: ManageIndex},
            {path: '/b/:slug/reports', Component: ReportsIndex},
          ].map(({exact = false, path, Component}) => (
            <Route
              key={path}
              exact={exact}
              path={path}
              render={(routeProps) => (
                <Component
                  user={user}
                  organization={organization}
                  {...routeProps}
                />
              )}
            />
          ))}
        </Switch>
      </Layout>
    </>
  );
}
